import { InvoiceData, PrintSettings, DispatchPrintTab, DispatchPrintOptions } from '@/types/invoice';
import { Tab, TabItem, PaymentMethod, Organization } from '@/types';
import { FEATURE_FLAGS } from '@/types/featureFlags';
import { isFeatureEnabled } from '@/lib/featureFlagsBridge';
import { calculateTabSaleTotals, toTabTotalsOrgSnapshot } from '@/utils/tabTotals';
import {
  createTabInvoiceTemplate,
  createDispatchOrderTemplate,
  createCashierDispatchCopyTemplate,
} from '@/services/printers/epson';
import {
  getCashierPrinterConfig,
  hasPrinterForDuty,
  printCashierDispatchCopy,
  printTemplateByDuty,
} from '@/services/printers';
import { PrinterConfig } from '@/services/printers/types';
import { resolvePrintedInvoiceNumber } from './resolvePrintedInvoiceNumber';
import { resolvePrefacturaDollarTotal } from './resolvePrefacturaDollarTotal';
import { resolveTableName } from './resolveTableName';
import { resolveLegalIdentity } from './legalIdentity';
import { toast } from '@/hooks/use-toast';
import { getPaymentMethodSpanishName } from '@/utils/paymentUtils';

/** Group tab items by dispatch duty (kitchen vs bar) */
function groupItemsByPrintDuty(items: TabItem[]): {
  kitchen: TabItem[];
  bar: TabItem[];
} {
  const kitchen = items.filter((item) => item.product?.productCategory?.printInKitchen);
  const bar = items.filter((item) => item.product?.productCategory?.printInBar);
  return { kitchen, bar };
}

export class InvoiceService {
  static getCashierPrinterConfig(): PrinterConfig | null {
    return getCashierPrinterConfig();
  }

  private static invoiceDataForPrint(invoiceData: InvoiceData): InvoiceData {
    if (!invoiceData.paymentMethod) {
      return invoiceData;
    }

    return {
      ...invoiceData,
      paymentMethod: {
        ...invoiceData.paymentMethod,
        name: getPaymentMethodSpanishName(invoiceData.paymentMethod),
      },
    };
  }

  static async printInvoice(
    invoiceData: InvoiceData,
    settings: Partial<PrintSettings> = {}
  ): Promise<void> {
    try {
      const cashierPrinter = InvoiceService.getCashierPrinterConfig();
      if (!cashierPrinter) {
        toast({
          title: 'Printer Error',
          description: 'No printer assigned for cashier duties',
          variant: 'destructive',
        });
        return;
      }

      const cashierPrinterWithOrgSettings = settings.currency
        ? { ...cashierPrinter, currency: settings.currency }
        : cashierPrinter;

      const printableInvoiceData = InvoiceService.invoiceDataForPrint(invoiceData);

      const template = createTabInvoiceTemplate(printableInvoiceData, cashierPrinterWithOrgSettings);
      await printTemplateByDuty(template, 'cashier');
    } catch (error) {
      console.warn('Thermal printer failed', error);
      throw error;
    }
  }

  static async printDispatch(
    tab: DispatchPrintTab,
    organization: Organization,
    { variant }: DispatchPrintOptions,
  ): Promise<void> {
    try {
      const { kitchen, bar } = groupItemsByPrintDuty(tab.items);
      const hasKitchenPrinter = hasPrinterForDuty('kitchen');
      const hasBarPrinter = hasPrinterForDuty('bar');

      const tableName = resolveTableName(organization.id, tab);

      if (kitchen.length > 0 && hasKitchenPrinter) {
        const kitchenTemplate = createDispatchOrderTemplate(
          {
            organizationName: organization.name,
            tabId: tab.id,
            customerName: tab.customerName,
            tableName,
            createdAt: tab.createdAt,
            items: kitchen,
            serverName: tab.serverName,
          },
          'cocina',
          variant,
        );
        await printTemplateByDuty(kitchenTemplate, 'kitchen');
      }

      if (bar.length > 0 && hasBarPrinter) {
        const barTemplate = createDispatchOrderTemplate(
          {
            organizationName: organization.name,
            tabId: tab.id,
            customerName: tab.customerName,
            tableName,
            createdAt: tab.createdAt,
            items: bar,
            serverName: tab.serverName,
          },
          'bar',
          variant,
        );
        await printTemplateByDuty(barTemplate, 'bar');
      }

      const cashierCopyEnabled = isFeatureEnabled(
        organization.slug,
        FEATURE_FLAGS.CASHIER_DISPATCH_COPY,
      );
      if (cashierCopyEnabled && (kitchen.length > 0 || bar.length > 0)) {
        const copyTemplate = createCashierDispatchCopyTemplate(
          {
            organizationName: organization.name,
            tabId: tab.id,
            customerName: tab.customerName,
            tableName,
            createdAt: tab.createdAt,
            serverName: tab.serverName,
          },
          { kitchen, bar },
          variant,
        );
        await printCashierDispatchCopy(copyTemplate);
      }
    } catch (error) {
      console.warn('Thermal printer failed', error);
      throw error;
    }
  }

  // Creates the object with the invoice details. Invoice number comes from
  // the DB when online; offline or on assign failure it uses a local label.
  static async createInvoiceFromTab(
    tab: {
      id: string;
      customerName: string;
      items: TabItem[];
      discount: number,
      serviceChargeDisabled?: boolean,
      ivaDisabled?: boolean;
      tip?: number;
      tableId?: string | null;
      serverName?: string;
    },
    organization: Organization,
    paymentMethod?: PaymentMethod,
    forPreview: boolean = false,
    cashPaymentDetails?: { amountReceived: number; changeGiven: number }
  ): Promise<InvoiceData> {
    const invoiceNumber = await resolvePrintedInvoiceNumber(
      forPreview,
      tab,
      organization.id,
    );
    const tableName = resolveTableName(organization.id, tab);
    const legalIdentity = resolveLegalIdentity(organization.settings);

    const totals = calculateTabSaleTotals(tab as Tab, toTabTotalsOrgSnapshot(organization));
    const {
      subtotal,
      serviceCharge,
      serviceChargeRate,
      iva: ivaCharge,
      ivaRate,
      tip,
      total,
    } = totals;

    return {
      id: invoiceNumber,
      forPreview,
      organizationName: organization.name,
      organizationAddress: organization.address,
      organizationPhone: organization.phone,
      organizationEmail: organization.email,
      organizationSlogan: organization.slogan,
      organizationLegalName: legalIdentity.name,
      organizationLegalId: legalIdentity.idNumber,
      customerName: tab.customerName,
      tabId: tab.id,
      tableName,
      serverName: tab.serverName,
      items: tab.items,
      subtotal,
      discount: tab.discount,
      serviceCharge,
      serviceChargeRate,
      ivaCharge,
      ivaRate,
      tip,
      total,
      totalInDollars: resolvePrefacturaDollarTotal(total, {
        forPreview,
        currency: organization.currency,
        exchangeRate: organization.exchangeRate,
      }),
      paymentMethod,
      cashPaymentDetails,
      createdAt: new Date(),
      notes: forPreview ? 'Transacción completada exitosamente' : undefined,
    };
  }
}
